import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/data/categories";

const LANGS = [
  { code: "en", label: "English" },
  { code: "pl", label: "Polski" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setCatOpen(false);
    setLangOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const switchLang = (code: string) => {
    const parts = location.pathname.split("/");
    parts[1] = code;
    const next = parts.join("/");
    setLangOpen(false);
    setMobileOpen(false);
    navigate(next || `/${code}`);
  };

  const currentLang = LANGS.find((l) => l.code === lang);

  return (
    <header
      className={`sticky top-0 z-50 bg-background-50 border-b transition-shadow duration-300 ${
        scrolled || catOpen || langOpen || mobileOpen
          ? "border-background-300"
          : "border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="h-16 md:h-18 flex items-center justify-between gap-4">
          {/* Brand */}
          <Link to={`/${lang}`} className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-foreground-950 text-base whitespace-nowrap">
                {t("brand.name")}
              </span>
              <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                {t("brand.tagline")}
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 text-sm font-semibold text-foreground-900">
            <Link
              to={`/${lang}`}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap"
            >
              {t("nav.home")}
            </Link>
            <Link
              to={`/${lang}/article/usfans-shopping-guide`}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap"
            >
              {t("nav.shoppingGuide")}
            </Link>
            <button
              onClick={() => setCatOpen((v) => !v)}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap flex items-center gap-1"
            >
              {t("nav.categories")}
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            <Link
              to={`/${lang}/article/usfans-spreadsheet-guide`}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap"
            >
              {t("nav.guides")}
            </Link>
            <Link
              to={`/${lang}/safety`}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap"
            >
              {t("nav.safety")}
            </Link>
            <Link
              to={`/${lang}/faq`}
              className="px-3 py-2 rounded-md hover:bg-background-100 hover:text-foreground-950 transition whitespace-nowrap"
            >
              {t("nav.faq")}
            </Link>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="h-10 px-3 rounded-md border border-background-300 bg-background-50 hover:bg-background-100 text-sm text-foreground-800 flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-global-line"></i>
                <span className="hidden md:inline">{currentLang?.label ?? "English"}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-lg border border-background-200 bg-background-50 py-1 overflow-hidden">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => switchLang(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 whitespace-nowrap flex items-center justify-between ${
                        l.code === lang ? "text-primary-700 font-semibold" : "text-foreground-800"
                      }`}
                    >
                      {l.label}
                      {l.code === lang && <i className="ri-check-line"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex h-10 px-4 items-center rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition whitespace-nowrap"
            >
              {t("nav.buyNow")}
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-900"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Category dropdown */}
      {catOpen && (
        <div className="absolute left-0 right-0 top-full border-b border-background-200 bg-background-50">
          <div className="w-full px-4 md:px-6 py-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {categories.map((c) => (
                <a
                  key={c.name}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setCatOpen(false)}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-foreground-800 hover:bg-background-100 hover:text-foreground-950 transition"
                >
                  <span className="w-9 h-9 rounded-md overflow-hidden shrink-0">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" />
                  </span>
                  <span className="whitespace-nowrap truncate">{c.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to={`/${lang}`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.home")}
            </Link>
            <Link
              to={`/${lang}/article/usfans-shopping-guide`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.shoppingGuide")}
            </Link>
            <Link
              to={`/${lang}/article/usfans-spreadsheet-guide`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.guides")}
            </Link>
            <Link
              to={`/${lang}/safety`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.safety")}
            </Link>
            <Link
              to={`/${lang}/faq`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.faq")}
            </Link>
            <p className="px-3 pt-3 pb-1 text-xs uppercase tracking-wide text-foreground-500 font-semibold">
              {t("nav.categories")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((c) => (
                <a
                  key={c.name}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 p-2 rounded-md border border-background-200 bg-background-50 hover:border-primary-300 transition"
                >
                  <span className="w-10 h-10 rounded-md overflow-hidden shrink-0">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" />
                  </span>
                  <span className="text-xs font-medium text-foreground-800 leading-tight">{c.name}</span>
                </a>
              ))}
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 h-11 px-4 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm font-semibold"
            >
              {t("nav.buyNow")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}